import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useApiClient } from "../api/client";
import { ApiKey } from "../api/resources/apiKeys";
import { StandardButton } from "../components/StandardButton";
import { addToast } from "../components/Toast";
import { colors } from "../utils/style";

const SectionHeading = styled.h3`
  color: ${colors.text.primary};
  font-size: 14px;
  font-weight: 600;
  margin: 20px 0 4px;
`;

const SupportingText = styled.p`
  color: ${colors.text.secondary};
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 10px;
`;

const KeyRow = styled.div`
  border-top: 1px solid ${colors.border.secondary};
  padding: 12px 0;
`;

const KeyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 12px;
`;

const KeyInfo = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
`;

const KeyName = styled.span`
  color: ${colors.text.primary};
  font-size: 14px;
  font-weight: 500;
`;

const KeyMeta = styled.span`
  color: ${colors.text.secondary};
  font-size: 12px;
`;

const KeyValue = styled.code`
  display: block;
  color: ${colors.text.primary};
  background: ${colors.bg.secondary};
  border: 1px solid ${colors.border.secondary};
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  margin-top: 8px;
  overflow-wrap: anywhere;
  padding: 8px;
`;

const KeyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const TextButton = styled.button<{ $danger?: boolean }>`
  cursor: pointer;
  color: ${(props) => (props.$danger ? colors.text.negative : colors.text.accent)};
  font-size: 13px;
  background: transparent;
  border: 0;
  padding: 4px 0;
  :hover {
    text-decoration: underline;
  }
  :disabled {
    cursor: default;
    opacity: 0.5;
    text-decoration: none;
  }
`;

const CreateForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 16px;
  align-items: center;
`;

const NameInput = styled.input`
  font-size: 14px;
  min-width: min(240px, 100%);
  padding: 6px 8px;
  border: 1px solid ${colors.border.secondary};
  border-radius: 4px;
  background: ${colors.bg.primary};
  color: ${colors.text.primary};
  flex: 1;
`;

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Never";
  return new Date(dateStr).toLocaleDateString();
}

export function ApiKeysSection() {
  const apiClient = useApiClient();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [regeneratingKeyId, setRegeneratingKeyId] = useState<number | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const result = await apiClient.apiKeys.list();
      setKeys(result);
    } catch {
      addToast({ type: "warning", content: "Failed to load API keys" });
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const result = await apiClient.apiKeys.create(newKeyName.trim());
      setKeys((previousKeys) => [result, ...previousKeys]);
      setNewKeyName("");
      setShowCreateForm(false);
      addToast({ type: "info", content: "API key created and saved to your account" });
    } catch {
      addToast({ type: "warning", content: "Failed to create API key" });
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerate = async (key: ApiKey) => {
    if (!confirm(`Regenerate API key "${key.name}"? The old key will stop working immediately.`)) return;

    setRegeneratingKeyId(key.id);
    try {
      const regeneratedKey = await apiClient.apiKeys.regenerate(key.id);
      setKeys((previousKeys) =>
        previousKeys.map((previousKey) => (previousKey.id === regeneratedKey.id ? regeneratedKey : previousKey)),
      );
      addToast({ type: "info", content: `API key "${key.name}" regenerated` });
    } catch {
      addToast({ type: "warning", content: "Failed to regenerate API key" });
    } finally {
      setRegeneratingKeyId(null);
    }
  };

  const handleRevoke = async (keyId: number, keyName: string) => {
    if (!confirm(`Revoke API key "${keyName}"? This cannot be undone.`)) return;
    try {
      await apiClient.apiKeys.revoke(keyId);
      addToast({ type: "info", content: `API key "${keyName}" revoked` });
      setKeys((previousKeys) => previousKeys.filter((key) => key.id !== keyId));
    } catch {
      addToast({ type: "warning", content: "Failed to revoke API key" });
    }
  };

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      addToast({ type: "info", content: "API key copied to clipboard" });
    } catch {
      addToast({ type: "warning", content: "Could not copy API key" });
    }
  };

  const activeKeys = keys.filter((key) => key.active);

  return (
    <div>
      <SectionHeading>API keys</SectionHeading>
      <SupportingText>
        Create named keys for agents and tools that connect without a browser. Your keys stay available here to copy,
        regenerate, or revoke. See{" "}
        <a
          href="https://agents.ideaflow.app/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: colors.text.accent, textDecoration: "underline" }}
        >
          agents.ideaflow.app
        </a>{" "}
        for the agent guide.
      </SupportingText>

      {loading ? (
        <SupportingText>Loading API keys...</SupportingText>
      ) : (
        <>
          {activeKeys.map((key) => (
            <KeyRow key={key.id}>
              <KeyHeader>
                <KeyInfo>
                  <KeyName>{key.name}</KeyName>
                  <KeyMeta>
                    Created {formatDate(key.createdAt)}
                    {key.lastUsedAt ? ` \u00b7 Last used ${formatDate(key.lastUsedAt)}` : ""}
                  </KeyMeta>
                </KeyInfo>
              </KeyHeader>
              <KeyValue>{key.key ?? `${key.keyPrefix}…`}</KeyValue>
              {!key.key && (
                <KeyMeta>
                  This older key was stored securely as a hash. Regenerate it once to make it re-copyable.
                </KeyMeta>
              )}
              <KeyActions>
                {key.key && <TextButton onClick={() => copyKey(key.key!)}>Copy key</TextButton>}
                <TextButton disabled={regeneratingKeyId === key.id} onClick={() => handleRegenerate(key)}>
                  {regeneratingKeyId === key.id ? "Regenerating..." : "Regenerate"}
                </TextButton>
                <TextButton $danger onClick={() => handleRevoke(key.id, key.name)}>
                  Revoke
                </TextButton>
              </KeyActions>
            </KeyRow>
          ))}

          {activeKeys.length === 0 && <SupportingText>No API keys yet.</SupportingText>}

          {showCreateForm ? (
            <CreateForm>
              <NameInput
                aria-label="API key name"
                placeholder="Key name (for example, Claude Code)"
                value={newKeyName}
                onChange={(event) => setNewKeyName(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleCreate()}
                autoFocus
              />
              <StandardButton onClick={handleCreate} disabled={creating || !newKeyName.trim()}>
                {creating ? "Creating..." : "Create key"}
              </StandardButton>
              <StandardButton onClick={() => setShowCreateForm(false)}>Cancel</StandardButton>
            </CreateForm>
          ) : (
            <StandardButton style={{ marginTop: 8, marginBottom: 16 }} onClick={() => setShowCreateForm(true)}>
              Create API key
            </StandardButton>
          )}
        </>
      )}
    </div>
  );
}
